import { Box, IconButton, Typography, CircularProgress, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useLayoutEffect, useEffect, useMemo } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Iconify from "../../../../../../reusable-components/iconify";
import { StyledEditor } from "../../../../../../reusable-components/comment-editor/styles";
import OptionsButton from "../../mobile/OptionsButton";
import FileThumbnail from "../../../../../../reusable-components/file-thumbnail";
import 'quill-mention';
import 'quill-mention/dist/quill.mention.css';
import { submitTicket, ticketMessageSubmitErrorMessage } from "../TicketHelper";
import { useSelector } from "../../../../../../redux/store";
import AIAssistButton from '../../../../../../reusable-components/editor/AIAssistButton';
import { handleSetVerticalPosition } from '../../../../../../reusable-components/editor/handlePosition';


NewTicketMessageInput.propTypes = {
  ticketHtml: PropTypes.string,
  selectedTicketType: PropTypes.object,
  ticketTypeFieldGroups: PropTypes.array,
  newUsers: PropTypes.array,
  selectedFacility: PropTypes.object,
  ticketSubject: PropTypes.string,
  selectedTicketCategory: PropTypes.object,
  setIsTicketInfoOpen: PropTypes.func,
  possibleUsers: PropTypes.array,
  setIsInputFocused: PropTypes.func,
}

export default function NewTicketMessageInput({
  ticketHtml,
  selectedTicketType,
  ticketTypeFieldGroups,
  newUsers,
  selectedFacility,
  ticketSubject,
  selectedTicketCategory,
  setIsTicketInfoOpen,
  possibleUsers,
  setIsInputFocused
}) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const containerRef = useRef(null);
  const quillRef = useRef(null);

  const [messageToSend, setMessageToSend] = useState('');
  const [maxHeight, setMaxHeight] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [cursorVerticalPosition, setCursorVerticalPosition] = useState(0);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return undefined;

    const updateVerticalPosition = (range) => {
      const position = handleSetVerticalPosition(quill, range);
      setCursorVerticalPosition(position);
    };

    const handleTextChange = () => {
      const range = quill.getSelection();
      updateVerticalPosition(range);
    };

    quill.on('text-change', handleTextChange);
    quill.on('selection-change', updateVerticalPosition);

    // Cleanup
    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', updateVerticalPosition);
    };
  }, []);

  useLayoutEffect(() => {
    const updateMaxHeight = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const availableHeight = window.innerHeight - rect.top - 75;
      setMaxHeight(`${availableHeight}px`);
    };

    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);

  useEffect(() => {
    if (quillRef.current) 
      setTimeout(() => quillRef.current.focus(), 0);
  }, []);

  useEffect(() => { setMessageToSend(ticketHtml ?? "") }, [ticketHtml]);

  const removeAttachment = (fileToRemove) => setAttachments((prevAttachments) => prevAttachments.filter((file) => file !== fileToRemove));

  const mentionSource = () => (searchTerm, renderList) => {
    if (searchTerm?.length > 0) {
      const matches = [];
      for (let i = 0; i < possibleUsers?.length; i++) {
        const nameParts = possibleUsers[i].value.split(' ');
        const matchFound = nameParts.some((part) => part.toLowerCase().startsWith(searchTerm.toLowerCase()));
        if (matchFound) {
          matches.push(possibleUsers[i]);
          if (matches.length >= 5) break;
        }
      }
      renderList(matches, searchTerm);
    }
  };
    
  const modules = useMemo(() => ({
    toolbar: false,
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      isolateCharacter: true,
      mentionDenotationChars: ['@'],
      spaceAfterInsert: true,
      defaultMenuOrientation: 'top',
      positioningStrategy: 'fixed',
      showDenotationChar: false,
      source: mentionSource(),
    },
  }), []);

  return (
    <>
      <Box sx={{ position: 'relative', width: '100%', paddingLeft: 3}}>
        <AIAssistButton
          cursorVerticalPosition={cursorVerticalPosition}
          value={messageToSend}
          quillRef={quillRef}
        />
        <StyledEditor
          sx={{
            minHeight: 0,
            width: '100%',
            marginBottom: 0,
            overflow: 'auto',
            '& .ql-container.ql-snow': {
              maxHeight,
            },
            '& .ql-editor': {
              pb: 0,
              mb: 0,
            },
          }}
        >
          <ReactQuill
            theme="snow"
            value={messageToSend}
            ref={quillRef}
            onChange={(e) => setMessageToSend(e)}
            modules={modules}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
        </StyledEditor>
        <Box sx={{ display: 'flex', my: 1, justifyContent: 'flex-end', gap: 1 }}>
          <OptionsButton setIsTicketInfoOpen={setIsTicketInfoOpen} attachments={attachments} setAttachments={setAttachments} />
          <Tooltip title={ticketMessageSubmitErrorMessage(messageToSend, attachments, ticketSubject)} arrow>
            <span>
              <IconButton
                sx={{ width: 40, height: 28, minHeight: 28 }}
                color="primary"
                onClick={() => submitTicket(messageToSend, selectedTicketType, ticketTypeFieldGroups, ticketSubject, selectedFacility, selectedTicketCategory, attachments, newUsers, null,
                  setIsMessageSending, navigate, user, false)}
                disabled={!ticketSubject || isMessageSending || !messageToSend.trim() && !attachments?.length}
              >
                {isMessageSending ? <CircularProgress size={20} color="primary" /> : <Iconify icon="mdi:send" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {attachments.map((file) => (
        <Box sx={{ display: 'flex', borderRadius: '5px', backgroundColor: 'rgba(193, 201, 208, 0.2)', alignItems: "center", padding: .5, my: .3 }} key={file.name}>
          <FileThumbnail sx={{ alignItems: "center", display: "flex" }} file={file} tooltip />
          <Typography sx={{ px: 1, color: (theme) => theme.palette.grey[700] }} variant="body2">{file.name}</Typography>
          <Iconify icon="mdi:close" onClick={() => removeAttachment(file)} sx={{ ml: 'auto', mr: 0 }} />
        </Box>
      ))}
    </>);
}